import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {FileText, TruckIcon, ArchiveIcon} from 'lucide-react';
import Topbar from '../components/Topbar';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Componente Dashboard
const Dashboard = () => {
  // Estado para shipments y inbounds
  const [shipments, setShipments] = useState([]);
  const [inbounds, setInbounds] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  
  // Estados UI
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Inicio');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Estados para filtros
  const [filter, setFilter] = useState({
    reference: '',
    pickupDateRange: [null, null],
    deliveryDateRange: [null, null],
    customer: '',
    status: '',
    type: '', // 'OUTBOUND' o 'INBOUND'
  });
  
  const navigate = useNavigate();

  // Función para procesar las fechas de entrega basado en las órdenes para Outbound
  const processDeliveryDates = (shipment) => {
    if (!shipment.orders || shipment.orders.length === 0) {
      return null;
    }

    // Ordenar órdenes por número de parada
    const sortedOrders = [...shipment.orders].sort((a, b) => {
      const stopA = a.Stop || Number.MAX_SAFE_INTEGER;
      const stopB = b.Stop || Number.MAX_SAFE_INTEGER;
      return stopA - stopB;
    });

    // Encontrar la fecha más temprana de entrega
    const validDates = sortedOrders
      .filter(order => order.Appointment)
      .map(order => {
        if (order.Appointment && order.Appointment_hr) {
          return new Date(`${order.Appointment.split('T')[0]}T${order.Appointment_hr}`);
        }
        return new Date(order.Appointment);
      });

    return validDates.length > 0 ? validDates[0] : null;
  };

  // Función para organizar clientes por parada
  const processCustomers = (shipment) => {
    if (!shipment.orders || shipment.orders.length === 0) {
      return shipment.customer || 'N/A';
    }

    // Ordenar órdenes por número de parada
    const sortedOrders = [...shipment.orders].sort((a, b) => {
      const stopA = a.Stop || Number.MAX_SAFE_INTEGER;
      const stopB = b.Stop || Number.MAX_SAFE_INTEGER;
      return stopA - stopB;
    });

    // Extraer clientes únicos manteniendo el orden
    const uniqueCustomers = [];
    const seenCustomers = new Set();

    sortedOrders.forEach(order => {
      const customerName = order.customer || order.Customer || 'N/A';
      if (!seenCustomers.has(customerName)) {
        uniqueCustomers.push(customerName);
        seenCustomers.add(customerName);
      }
    });

    return uniqueCustomers.join(' - ');
  };

  // Función para organizar destinos por parada
  const processDestinations = (shipment) => {
    if (!shipment.orders || shipment.orders.length === 0) {
      return shipment.destination || 'N/A';
    }

    // Ordenar órdenes por número de parada
    const sortedOrders = [...shipment.orders].sort((a, b) => {
      const stopA = a.Stop || Number.MAX_SAFE_INTEGER;
      const stopB = b.Stop || Number.MAX_SAFE_INTEGER;
      return stopA - stopB;
    });

    // Extraer destinos únicos manteniendo el orden
    const uniqueDestinations = [];
    const seenDestinations = new Set();

    sortedOrders.forEach(order => {
      const destination = order.City && order.State 
        ? `${order.City}, ${order.State}` 
        : order.City || 'N/A';
      
      if (!seenDestinations.has(destination)) {
        uniqueDestinations.push(destination);
        seenDestinations.add(destination);
      }
    });

    return uniqueDestinations.join(' - ');
  };

  const fetchShipments = async () => {
    try {
      setLoading(true);
      // Obtener shipments con información detallada de órdenes
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/shipments`);
      
      // Obtener detalles de órdenes para cada embarque
      const shipmentsWithOrders = await Promise.all(
        response.data.map(async (shipment) => {
          try {
            // Solo obtener detalles completos si tiene ID
            if (shipment.id) {
              const orderDetails = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v1/shipments/${shipment.id}/with-orders`

              );
              return orderDetails.data;
            }
            return shipment;
          } catch (err) {
            console.error(`Error fetching orders for shipment ${shipment.id}:`, err);
            return shipment;
          }
        })
      );
      
      // Formatear los datos de shipments y agregar tipo
      const formattedShipments = shipmentsWithOrders.map(shipment => {
        // Procesar fechas de entrega y clientes basados en órdenes
        const delivery_date = processDeliveryDates(shipment);
        const processedCustomer = processCustomers(shipment);
        const processedDestination = processDestinations(shipment);
        
        return {
          ...shipment,
          type: 'OUTBOUND',
          reference: shipment.reference || 'N/A',
          customer: processedCustomer,
          origin: shipment.origin || 'N/A',
          destination: processedDestination,
          status: shipment.status || 'PENDIENTE',
          pickup_date: shipment.pickup_date || null,
          delivery_date: delivery_date || shipment.delivery_date || null,
        };
      });
      
      setShipments(formattedShipments);
      return formattedShipments;
    } catch (error) {
      console.error('Error al obtener los embarques:', error);
      setError('Error al cargar los embarques. Intente nuevamente.');
      return [];
    }
  };

  const fetchInbound = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/inbound/shipments`);
      
      // Formatear los datos de inbound y agregar tipo
      const formattedInbound = response.data.map(inbound => {
        // Determinar cliente/cuenta a partir de los contenedores (ordenados por prioridad)
        const customers = inbound.contenedores && inbound.contenedores.length > 0
          ? [...new Set(inbound.contenedores.map(c => c.nombre_cuenta))].join(' - ')
          : 'N/A';
        
        // Determinar origen a partir de los contenedores (ordenados por prioridad)
        const origins = inbound.contenedores && inbound.contenedores.length > 0
          ? [...new Set(inbound.contenedores.map(c => c.nombre_origen))].join(' - ')
          : 'N/A';
        
        // Determinar destino a partir de los contenedores (ordenados por prioridad)
        const destinations = inbound.contenedores && inbound.contenedores.length > 0
          ? [...new Set(inbound.contenedores.filter(c => c.ubicacion).map(c => c.ubicacion))].join(' - ')
          : 'N/A';
        
        // Determinar la fecha de entrega más cercana
        const deliveryDates = inbound.contenedores && inbound.contenedores.length > 0
          ? inbound.contenedores.filter(c => c.hora_de_cita).map(c => new Date(c.hora_de_cita))
          : [];
        
        const earliestDeliveryDate = deliveryDates.length > 0
          ? new Date(Math.min(...deliveryDates.map(date => date.getTime())))
          : null;
        
        return {
          ...inbound,
          type: 'INBOUND',
          reference: `IN-${inbound.id}` || 'N/A',
          customer: customers,
          origin: origins,
          destination: destinations,
          status: inbound.estatus || 'PENDIENTE',
          pickup_date: inbound.fecha_de_carga || null,
          delivery_date: earliestDeliveryDate,
        };
      });
      
      setInbounds(formattedInbound);
      return formattedInbound;
    } catch (error) {
      console.error('Error al obtener los viajes de inbound:', error);
      setError('Error al cargar los viajes de inbound. Intente nuevamente.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      const shipmentsData = await fetchShipments();
      const inboundsData = await fetchInbound();
      
      // Combinar y ordenar los datos por fecha de pickup más reciente primero
      const combined = [...shipmentsData, ...inboundsData].sort((a, b) => {
        if (!a.pickup_date) return 1;
        if (!b.pickup_date) return -1;
        return new Date(b.pickup_date) - new Date(a.pickup_date);
      });
      
      setCombinedData(combined);
      setLoading(false);
    };

    loadAllData();
    
    // Configurar actualización periódica cada 5 minutos
    const intervalId = setInterval(() => {
      loadAllData();
    }, 5 * 60 * 1000);

    // Limpiar intervalo al desmontar componente
    return () => clearInterval(intervalId);
  }, []);

  // Funcion auxiliar para normalizar fechas (eliminar la componente de zona horaria)
  const normalizeDate = (date) => {
    if (!date) return null;
    const normalizedDate = new Date(date);
    return new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
  };

  // Función auxiliar para verificar si una fecha está en un rango
  const isDateInRange = (date, startDate, endDate) => {
    if (!date) return false;
    
    // Normalizar todas las fechas para evitar problemas de zona horaria
    const normalizedDate = normalizeDate(date);
    const normalizedStartDate = normalizeDate(startDate);
    const normalizedEndDate = normalizeDate(endDate);
    
    // Comprobar si está en el rango
    return (
      normalizedDate >= normalizedStartDate && 
      normalizedDate <= normalizedEndDate
    );
  };

  // Aplicar filtros a los datos combinados
  const filteredData = combinedData.filter((item) => {
    const matchesReference = !filter.reference || 
      (item.reference?.toLowerCase().includes(filter.reference.toLowerCase()) ?? false);
    
    const matchesCustomer = !filter.customer || 
      (item.customer?.toLowerCase().includes(filter.customer.toLowerCase()) ?? false);
    
    // Filtro de fecha de carga (rango)
    let matchesPickupDate = true;
    if (filter.pickupDateRange[0] && filter.pickupDateRange[1]) {
      matchesPickupDate = isDateInRange(
        item.pickup_date, 
        filter.pickupDateRange[0], 
        filter.pickupDateRange[1]
      );
    }

    // Filtro de fecha de entrega (rango)
    let matchesDeliveryDate = true;
    if (filter.deliveryDateRange[0] && filter.deliveryDateRange[1]) {
      matchesDeliveryDate = isDateInRange(
        item.delivery_date, 
        filter.deliveryDateRange[0], 
        filter.deliveryDateRange[1]
      );
    }

    const matchesStatus = !filter.status || 
      (item.status?.toLowerCase() === filter.status.toLowerCase());

    const matchesType = !filter.type || 
      item.type === filter.type;

    return matchesReference && matchesCustomer && matchesPickupDate && 
           matchesDeliveryDate && matchesStatus && matchesType;
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handlers para los date pickers
  const handlePickupDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFilter((prev) => ({
      ...prev,
      pickupDateRange: [start, end],
    }));
  };

  const handleDeliveryDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFilter((prev) => ({
      ...prev,
      deliveryDateRange: [start, end],
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setTimeout(() => {
      navigate('/login');
    }, 0); 
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  // Función para obtener el estilo del badge según el tipo y estado
  const getStatusBadgeStyle = (item) => {
    // Outbound badges
    if (item.type === 'OUTBOUND') {
      switch (item.status?.toUpperCase()) {
        case 'TRANSITO': return 'bg-blue-500 text-white';
        case 'FINALIZADO': return 'bg-green-700 text-white';
        case 'PENDIENTE': return 'bg-yellow-100 text-yellow-800';
        case 'ARRIBADO': return 'bg-sky-600 text-white';
        case 'EN CLIENTE': return 'bg-indigo-500 text-white';
        case 'ASIGNADO': return 'bg-purple-500 text-white';
        case 'CANCELADO': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
    
    // Inbound badges - usando colores distintos para diferenciar
    switch (item.status?.toLowerCase()) {
      case 'pendiente': return 'bg-blue-100 text-blue-800';
      case 'hora de arribo a carga': return 'bg-orange-500 text-white';
      case 'liberacion de carga': return 'bg-yellow-500 text-white';
      case 'arribo a descargar': return 'bg-green-500 text-white';
      case 'liberacion de descarga': return 'bg-lime-500 text-white';
      case 'arribo a terminar vacio': return 'bg-red-500 text-white';
      case 'salida de terminal': return 'bg-gray-500 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Mapeo de estados para mostrar nombres más amigables (específico de Inbound)
  const getStatusName = (item) => {
    if (item.type === 'OUTBOUND') {
      return item.status || 'N/A';
    }
    
    // Para Inbound
    const statusNames = {
      "pendiente": "Creado",
      "hora de arribo a carga": "Cargado",
      "liberacion de carga": "Transito",
      "arribo a descargar": "En destino",
      "liberacion de descarga": "Transito vacio",
      "arribo a terminar vacio": "En puerta vacio",
      "salida de terminal": "Finalizado"
    };
    
    return statusNames[item.status?.toLowerCase()] || item.status || 'N/A';
  };

  // Calcular estadísticas combinadas
  const stats = {
    totalShipments: combinedData.length,
    inTransit: combinedData.filter(item => 
      (item.type === 'OUTBOUND' && item.status === 'TRANSITO') || 
      (item.type === 'INBOUND' && item.status?.toLowerCase() === 'liberacion de carga')
    ).length,
    delivered: combinedData.filter(item => 
      (item.type === 'OUTBOUND' && item.status === 'FINALIZADO') || 
      (item.type === 'INBOUND' && item.status?.toLowerCase() === 'salida de terminal')
    ).length,
    pending: combinedData.filter(item => 
      (item.type === 'OUTBOUND' && ['PENDIENTE', 'CREADO'].includes(item.status)) || 
      (item.type === 'INBOUND' && item.status?.toLowerCase() === 'pendiente')
    ).length,
    outbound: combinedData.filter(item => item.type === 'OUTBOUND').length,
    inbound: combinedData.filter(item => item.type === 'INBOUND').length,
  };

  return (
    <div className="flex">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />

      {/* Contenido principal */}
      <div
        className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}
      >
        {/* Replace with Topbar component */}
        <Topbar 
          title="Dashboard" 
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            setFilter({...filter, reference: value});
          }}
        />

        {/* Barra de filtros */}
        <div className="flex flex-wrap gap-3 mb-6 mt-6">
          <input
            type="text"
            name="reference"
            value={filter.reference}
            onChange={handleFilterChange}
            placeholder="Filtrar por referencia"
            className="p-2 border rounded-md"
          />
          <DatePicker
            selected={filter.pickupDateRange[0]}
            onChange={handlePickupDateRangeChange}
            startDate={filter.pickupDateRange[0]}
            endDate={filter.pickupDateRange[1]}
            selectsRange
            placeholderText="Rango de fecha de carga"
            className="p-2 border rounded-md"
          />
          <DatePicker
            selected={filter.deliveryDateRange[0]}
            onChange={handleDeliveryDateRangeChange}
            startDate={filter.deliveryDateRange[0]}
            endDate={filter.deliveryDateRange[1]}
            selectsRange
            placeholderText="Rango de fecha de entrega"
            className="p-2 border rounded-md"
          />
          <input
            type="text"
            name="customer"
            value={filter.customer}
            onChange={handleFilterChange}
            placeholder="Filtrar por cliente"
            className="p-2 border rounded-md"
          />
          <select
            name="status"
            value={filter.status}
            onChange={handleFilterChange}
            className="p-2 border rounded-md"
          >
            <option value="">Todos los Estatus</option>
            {/* Opciones para estados de Outbound */}
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="TRANSITO">TRANSITO</option>
            <option value="FINALIZADO">FINALIZADO</option>
            <option value="ARRIBADO">ARRIBADO</option>
            <option value="EN CLIENTE">EN CLIENTE</option>
            {/* Opciones para estados de Inbound */}
            <option value="pendiente">Creado (Inbound)</option>
            <option value="hora de arribo a carga">Cargado (Inbound)</option>
            <option value="liberacion de carga">Transito (Inbound)</option>
            <option value="arribo a descargar">En destino (Inbound)</option>
            <option value="liberacion de descarga">Transito vacio (Inbound)</option>
            <option value="salida de terminal">Finalizado (Inbound)</option>
          </select>
          <select
            name="type"
            value={filter.type}
            onChange={handleFilterChange}
            className="p-2 border rounded-md"
          >
            <option value="">Todos los Tipos</option>
            <option value="OUTBOUND">OUTBOUND</option>
            <option value="INBOUND">INBOUND</option>
          </select>
        </div>

        {/* Sección de estadísticas */}
        <div className="mb-6 grid gap-6 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-6">
          <Card>
            <CardHeader className="bg-blue-600 text-white py-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.totalShipments}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="bg-yellow-600 text-white py-2">
              <CardTitle className="text-sm font-medium">En Tránsito</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.inTransit}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="bg-green-600 text-white py-2">
              <CardTitle className="text-sm font-medium">Finalizados</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.delivered}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="bg-red-600 text-white py-2">
              <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.pending}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="bg-indigo-600 text-white py-2">
              <CardTitle className="text-sm font-medium">Outbound</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.outbound}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="bg-purple-600 text-white py-2">
              <CardTitle className="text-sm font-medium">Inbound</CardTitle>
            </CardHeader>
            <CardContent className="bg-white text-center py-3">
              <h2 className="text-3xl">{stats.inbound}</h2>
            </CardContent>
          </Card>
        </div>

        {/* Tabla de embarques combinados */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-800 text-white">
            <CardTitle>Viajes Recientes</CardTitle>
          </CardHeader>
          <CardContent className="bg-white">
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-900"></div>
                <p className="ml-2">Cargando datos...</p>
              </div>
            ) : error ? (
              <p className="text-red-500 text-center p-4">{error}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHeader className="bg-gray-200">
                    <TableRow>
                      <TableHead className="px-4 py-2">Tipo</TableHead>
                      <TableHead className="px-4 py-2">Referencia</TableHead>
                      <TableHead className="px-4 py-2">Cliente/Cuenta</TableHead>
                      <TableHead className="px-4 py-2">Origen</TableHead>
                      <TableHead className="px-4 py-2">Destino</TableHead>
                      <TableHead className="px-4 py-2">Estado</TableHead>
                      <TableHead className="px-4 py-2">Fecha de Carga</TableHead>
                      <TableHead className="px-4 py-2">Fecha de Entrega</TableHead>
                      <TableHead className="px-4 py-2">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {filteredData.length > 0 ? (
                      filteredData.map((item, index) => (
                        <TableRow key={`${item.type}-${item.id}-${index}`} className="hover:bg-gray-50">
                          <TableCell className="px-4 py-2">
                            <Badge variant="outline" className={
                              item.type === 'OUTBOUND' 
                                ? 'bg-indigo-100 text-indigo-800 border-indigo-300' 
                                : 'bg-purple-100 text-purple-800 border-purple-300'
                            }>
                              {item.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-2">{item.reference}</TableCell>
                          <TableCell className="px-4 py-2">{item.customer}</TableCell>
                          <TableCell className="px-4 py-2">{item.origin}</TableCell>
                          <TableCell className="px-4 py-2">{item.destination}</TableCell>
                          <TableCell className="px-4 py-2">
                            <Badge className={`px-2 py-1 rounded-full text-sm ${getStatusBadgeStyle(item)}`}>
                              {getStatusName(item)}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-2">
                            {formatDate(item.pickup_date)}
                          </TableCell>
                          <TableCell className="px-4 py-2">
                            {formatDate(item.delivery_date)}
                          </TableCell>
                          <TableCell className="px-4 py-2">
                            <div className="flex space-x-2">
                              <Button
                                className={`p-2 rounded ${
                                  item.type === 'OUTBOUND' 
                                    ? 'bg-indigo-600 hover:bg-indigo-700' 
                                    : 'bg-purple-600 hover:bg-purple-700'
                                } text-white`}
                                onClick={() => navigate(
                                  item.type === 'OUTBOUND' 
                                    ? `/shipments` 
                                    : `/inbound`
                                )}
                                title={`Ver detalles de ${item.type}`}
                              >
                                {item.type === 'OUTBOUND' ? <TruckIcon size={16} /> : <ArchiveIcon size={16} />}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-4">
                          No se encontraron viajes que coincidan con los criterios de búsqueda.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;